import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Paper,
  Snackbar,
  IconButton,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Avatar,
  useTheme,
  useMediaQuery,
  Skeleton,
  Alert,
  Tooltip,
  Fab,
  CircularProgress,
  Checkbox,
  Divider,
  LinearProgress,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  Timeline as TimelineIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Cancel as CancelIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Flag as FlagIcon,
  Send as SendIcon,
  Link as LinkIcon,
  LocalShipping as DeliveryIcon,
  FileDownload as DownloadIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import config from '../config';
import DetalleDialog from '../components/DetalleDialog';

// Estados con configuración de colores
const estadosConfig = {
  1: { nombre: 'Pendiente', color: '#FB8C00', bgColor: '#FFF3E0', icon: WarningIcon },
  2: { nombre: 'Asignado', color: '#1E88E5', bgColor: '#E3F2FD', icon: PersonIcon },
  3: { nombre: 'En Camino', color: '#7B1FA2', bgColor: '#F3E5F5', icon: LocationIcon },
  4: { nombre: 'En Proceso', color: '#43A047', bgColor: '#E8F5E8', icon: PlayArrowIcon },
  5: { nombre: 'Terminado', color: '#2E7D32', bgColor: '#E8F5E8', icon: CheckCircleIcon },
  6: { nombre: 'En Espera', color: '#FFA000', bgColor: '#FFF8E1', icon: PauseIcon },
  7: { nombre: 'Cancelado', color: '#C62828', bgColor: '#FFEBEE', icon: CancelIcon }
};

// Componente de Progreso de Orden
const ProgresoOrden = ({ orden }) => {
  const indicePorcentaje = {
    1: 20, 2: 40, 3: 60, 4: 80, 5: 100, 6: 10, 7: 0
  };
  
  const progreso = indicePorcentaje[parseInt(orden?.id_estado_servicio)] || 0;
  const color = orden?.id_estado_servicio === '7' ? 'error' : progreso === 100 ? 'success' : 'primary';

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Progreso
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {progreso}%
        </Typography>
      </Box>
      <LinearProgress 
        variant="determinate" 
        value={progreso} 
        color={color}
        sx={{ height: 6, borderRadius: 3 }}
      />
    </Box>
  );
};


// Componente de Tarjeta de Orden para vista móvil
const OrdenCard = ({ orden, onView, onEdit, onDelete }) => {
  const estadoConfig = estadosConfig[parseInt(orden.id_estado_servicio)] || {};
  const IconComponent = estadoConfig.icon || AssignmentIcon;
  const isDelivery = orden.tipo_servicio?.toLowerCase().includes('delivery');
  const isPadre = isDelivery && !orden.id_orden_padre;
  const isHija = isDelivery && !!orden.id_orden_padre;

  return (
    <Card sx={{
      mb: 2,
      borderRadius: 3,
      border: '1px solid rgba(26, 35, 126, 0.1)',
      borderLeft: `4px solid ${isPadre ? '#1A237E' : isHija ? '#C7D2FE' : (estadoConfig.color || '#1976d2')}`,
      bgcolor: isPadre ? 'rgba(30,39,131,0.03)' : 'white',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 25px rgba(26, 35, 126, 0.15)'
      }
    }}>
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1A237E' }}>
              OS #{orden.id_orden_servicio}
            </Typography>
            {isPadre && <Chip label="MASTER" size="small" sx={{ height: 20, fontSize: 9, fontWeight: 800, bgcolor: '#1A237E', color: '#fff' }} />}
            {isHija && <Chip label={`(${orden.id_orden_padre})`} size="small" sx={{ height: 20, fontSize: 10, bgcolor: '#EEF2FF', color: '#1A237E' }} />}
          </Box>
          <Chip 
            icon={<IconComponent sx={{ fontSize: 16 }} />}
            label={estadoConfig.nombre || orden.nombre_estado_servicio}
            size="small"
            sx={{ 
              bgcolor: estadoConfig.bgColor,
              color: estadoConfig.color,
              fontWeight: 'bold'
            }}
          />
        </Box>

        {/* Cliente */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Cliente
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {orden.nombre_cliente} {orden.apellido_cliente}
          </Typography>
        </Box>

        {/* Tipo de servicio */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Tipo de Servicio
          </Typography>
          <Typography variant="body2">
            {orden.tipo_servicio || 'No especificado'}
          </Typography>
        </Box>

        {/* Progreso */}
        <ProgresoOrden orden={orden} />

        {/* Personal asignado */}
        {orden.trabajadores && orden.trabajadores.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Personal Asignado
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {orden.trabajadores.slice(0, 2).map((trabajador, index) => (
                <Chip
                  key={index}
                  avatar={<Avatar sx={{ bgcolor: '#1A237E' }}>{trabajador.nombre_trabajador?.[0]}</Avatar>}
                  label={`${trabajador.nombre_trabajador} ${trabajador.apellido_trabajador}`}
                  size="small"
                  variant="outlined"
                />
              ))}
              {orden.trabajadores.length > 2 && (
                <Chip
                  label={`+${orden.trabajadores.length - 2} más`}
                  size="small"
                  color="primary"
                />
              )}
            </Box>
          </Box>
        )}

        {/* Fecha */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, color: 'text.secondary' }}>
          <ScheduleIcon sx={{ mr: 1, fontSize: 16 }} />
          <Typography variant="caption">
            {new Date(orden.fecha_orden).toLocaleString()}
          </Typography>
        </Box>

        {/* Acciones */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Tooltip title="Ver detalles">
            <IconButton 
              size="small" 
              onClick={() => onView(orden)}
              sx={{ bgcolor: 'info.light', color: 'white', '&:hover': { bgcolor: 'info.main' } }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar orden">
            <IconButton 
              size="small" 
              onClick={() => onEdit(orden)}
              sx={{ bgcolor: 'primary.light', color: 'white', '&:hover': { bgcolor: 'primary.main' } }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar orden">
            <IconButton 
              size="small" 
              onClick={() => onDelete(orden)}
              sx={{ bgcolor: 'error.light', color: 'white', '&:hover': { bgcolor: 'error.main' } }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );
};

function OrdenesServicioAdmin() {
  const [ordenes, setOrdenes] = useState([]);
  const [filteredOrdenes, setFilteredOrdenes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrden, setSelectedOrden] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20);
  const [orderBy, setOrderBy] = useState('id_orden_servicio');
  const [order, setOrder] = useState('desc');
  const [error, setError] = useState(null);
  const [sendLinkDialog, setSendLinkDialog] = useState({ open: false, orden: null });
  const [sendLinkEmail, setSendLinkEmail] = useState('');
  const [sendLinkLoading, setSendLinkLoading] = useState(false);
  const [trackingPreview, setTrackingPreview] = useState(null);
  const [trackingPreviewLoading, setTrackingPreviewLoading] = useState(false);
  const [deliveryEmails, setDeliveryEmails] = useState([]); // [{id_orden_servicio, email}]
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', severity: 'success' });

  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    fetchOrdenes();
  }, []);

  useEffect(() => {
    const filterAndSortOrdenes = () => {
      let filtered = [...ordenes];
      
      if (searchTerm) {
        filtered = filtered.filter(orden =>
          Object.values(orden)
            .join(' ')
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        );
      }

      filtered.sort((a, b) => {
        const isAsc = order === 'asc';
        if (a[orderBy] < b[orderBy]) return isAsc ? -1 : 1;
        if (a[orderBy] > b[orderBy]) return isAsc ? 1 : -1;
        return 0;
      });

      setFilteredOrdenes(filtered);
      setPage(0);
    };

    filterAndSortOrdenes();
  }, [ordenes, searchTerm, order, orderBy]);

  const fetchOrdenes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/ordenes`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setOrdenes(response.data);
      setError(null);
    } catch (error) {
      console.error('Error al obtener las órdenes de servicio:', error);
      setError('Error al cargar las órdenes. Por favor, inténtelo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const openSendLinkDialog = async (orden) => {
    console.log('>>> openSendLinkDialog llamado, orden:', orden.id_orden_servicio, 'tipo:', orden.tipo_servicio, 'id_orden_padre:', orden.id_orden_padre);
    setSendLinkDialog({ open: true, orden });
    setSendLinkEmail('');
    setTrackingPreview(null);
    setDeliveryEmails([]);
    setTrackingPreviewLoading(true);
    try {
      const url = `${config.API_BASE_URL}/api/v1/delivery-rutas/${orden.id_orden_servicio}/tracking-preview`;
      console.log('>>> Llamando preview:', url);
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${user.token}` } });
      console.log('>>> Preview response:', res.data);
      setTrackingPreview(res.data);
      if (res.data.es_delivery) {
        console.log('>>> Es delivery, destinatarios:', res.data.destinatarios?.length);
        setDeliveryEmails(res.data.destinatarios.map(d => ({
          id_orden_servicio: d.id_orden_servicio,
          email: d.email || '',
          checked: !!(d.email),
        })));
      } else if (res.data.destinatarios?.[0]?.email) {
        setSendLinkEmail(res.data.destinatarios[0].email);
      }
    } catch (err) {
      console.error('>>> Error preview:', err.response?.status, err.response?.data, err.message);
    } finally {
      setTrackingPreviewLoading(false);
    }
  };

  const handleSendLink = async () => {
    if (!sendLinkDialog.orden) return;
    setSendLinkLoading(true);
    try {
      if (trackingPreview?.es_delivery) {
        // Delivery: enviar con emails editados
        const res = await axios.post(
          `${config.API_BASE_URL}/api/v1/delivery-rutas/${sendLinkDialog.orden.id_orden_servicio}/enviar-tracking`,
          { emails_override: deliveryEmails.filter(e => e.checked) },
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
        setSendLinkDialog({ open: false, orden: null });
        setSnackbar({ open: true, msg: res.data.mensaje, severity: 'success' });
      } else {
        if (!sendLinkEmail) return;
        await axios.post(`${config.API_BASE_URL}/api/v1/tracking-links/generar`, {
          id_orden_servicio: sendLinkDialog.orden.id_orden_servicio,
          email_destino: sendLinkEmail,
          enviar_email: true,
        }, { headers: { Authorization: `Bearer ${user.token}` } });
        setSendLinkDialog({ open: false, orden: null });
        setSnackbar({ open: true, msg: `Link enviado a ${sendLinkEmail}`, severity: 'success' });
      }
    } catch (err) {
      setSnackbar({ open: true, msg: err.response?.data?.error || 'Error al enviar', severity: 'error' });
    } finally {
      setSendLinkLoading(false);
    }
  };

  const handleDeliveryEmailChange = (idOrden, value) => {
    setDeliveryEmails(prev => prev.map(e => e.id_orden_servicio === idOrden ? { ...e, email: value } : e));
  };

  const handleDeliveryCheckToggle = (idOrden) => {
    setDeliveryEmails(prev => prev.map(e => e.id_orden_servicio === idOrden ? { ...e, checked: !e.checked } : e));
  };

  const getWhatsAppLink = (telefono, nombre, idOrden) => {
    const num = (telefono || '').replace(/[^0-9]/g, '');
    if (!num) return null;
    const msg = encodeURIComponent(`Hola ${nombre || ''}, te comparto el link de seguimiento de tu pedido (Orden #${idOrden}). Lo recibirás por email en breve.`);
    return `https://web.whatsapp.com/send?phone=${num}&text=${msg}`;
  };

  const handleDelete = async (orden) => {
    if (window.confirm(`¿Estás seguro de desactivar la orden ${orden.id_orden_servicio}?`)) {
      try {
        await axios.delete(`${config.API_BASE_URL}/api/ordenes/${orden.id_orden_servicio}`, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        fetchOrdenes();
      } catch (error) {
        console.error('Error al desactivar la orden de servicio:', error);
        setError('Error al eliminar la orden.');
      }
    }
  };

  // Helpers para delivery
  const esDelivery = (orden) => orden.tipo_servicio?.toLowerCase().includes('delivery');
  const esPadre = (orden) => esDelivery(orden) && !orden.id_orden_padre;
  const esHija = (orden) => esDelivery(orden) && !!orden.id_orden_padre;

  const handleView = (orden) => {
    if (esPadre(orden)) {
      navigate(`/home/delivery/${orden.id_orden_servicio}`);
      return;
    }
    if (esHija(orden)) {
      navigate(`/home/delivery/${orden.id_orden_padre}`);
      return;
    }
    setSelectedOrden(orden);
    setOpenDialog(true);
  };

  const handleEdit = (orden) => {
    if (esPadre(orden)) {
      navigate(`/home/delivery/${orden.id_orden_servicio}`);
      return;
    }
    if (esHija(orden)) {
      navigate(`/home/delivery/${orden.id_orden_padre}`);
      return;
    }
    navigate(`/home/ordenes/editar/${orden.id_orden_servicio}`);
  };

  // Exportar CSV
  const handleExportCSV = () => {
    const headers = [
      'ID', 'Tipo', 'Padre', 'Cliente', 'Fecha', 'Hora', 'Estado',
      'Local', 'Periodo', 'Dirección Origen', 'Servicio', 'Precio Total',
      'Tracker', 'Comisión', 'Detalle Entrega', 'Secuencia'
    ];

    const rows = filteredOrdenes.map(o => {
      const tracker = o.trabajadores?.[0];
      const servicio = o.detalles?.[0];
      return [
        o.id_orden_servicio,
        o.id_orden_padre ? `Entrega (${o.id_orden_padre})` : (esDelivery(o) ? 'MASTER' : (o.tipo_servicio || '')),
        o.id_orden_padre || '',
        `${o.nombre_cliente || ''} ${o.apellido_cliente || ''}`.trim(),
        o.fecha_orden ? new Date(o.fecha_orden).toLocaleDateString() : '',
        o.fecha_orden ? new Date(o.fecha_orden).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '',
        estadosConfig[parseInt(o.id_estado_servicio)]?.nombre || o.nombre_estado_servicio || '',
        o.nombre_local || '',
        o.mes && o.anio ? `${o.mes}/${o.anio}` : '',
        o.direccion_origen || '',
        servicio?.nombre_servicio || '',
        o.precio_total || 0,
        tracker ? `${tracker.nombre_trabajador} ${tracker.apellido_trabajador}` : '',
        tracker?.comision || '',
        o.detalle_entrega || '',
        o.orden_secuencia || '',
      ].map(v => `"${String(v).replace(/"/g, '""')}"`);
    });

    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ordenes_servicio_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const createSortHandler = (property) => () => {
    handleRequestSort(property);
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', p: { xs: 2, sm: 3, md: 4 } }}>
        <Skeleton variant="text" width="60%" height={60} sx={{ mb: 3 }} />
        <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: 3 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: '100%',
      p: { xs: 2, sm: 3, md: 4 }
    }}>
      
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 4
      }}>
        <Box>
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            component="h1"
            sx={{ 
              fontWeight: 'bold',
              color: '#1A237E',
              mb: 0.5
            }}
          >
            🎯 Gestión de Órdenes de Servicio
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Administra y supervisa todas las órdenes de servicio
          </Typography>
        </Box>

        {!isMobile && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportCSV}
              disabled={filteredOrdenes.length === 0}
              sx={{
                borderRadius: 2,
                px: 2,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 'bold',
                borderColor: '#1A237E',
                color: '#1A237E',
                '&:hover': { borderColor: '#0D47A1', bgcolor: 'rgba(26,35,126,0.04)' },
              }}
            >
              Exportar CSV
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/home/ordenes/crear')}
              sx={{
                background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
                borderRadius: 2,
                px: 3,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 'bold',
                boxShadow: '0 4px 15px rgba(26, 35, 126, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)',
                  boxShadow: '0 6px 20px rgba(26, 35, 126, 0.4)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Crear Orden
            </Button>
          </Box>
        )}
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Barra de búsqueda */}
      <Box sx={{ mb: 3 }}>
        <TextField
          variant="outlined"
          fullWidth={isMobile}
          size="small"
          placeholder="Buscar por número, cliente, estado..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#4FC3F7' }} />
              </InputAdornment>
            ),
          }}
          sx={{ 
            width: isMobile ? '100%' : 400,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&:hover fieldset': {
                borderColor: '#4FC3F7',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#1A237E',
              },
            }
          }}
        />
      </Box>

      {/* Vista móvil - Cards */}
      {isMobile ? (
        <Box>
          {filteredOrdenes
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((orden) => (
              <OrdenCard 
                key={orden.id_orden_servicio} 
                orden={orden}
                onView={handleView}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          
          {filteredOrdenes.length > rowsPerPage && (
            <Paper sx={{ mt: 2, borderRadius: 2 }}>
              <TablePagination
                rowsPerPageOptions={[20]}
                component="div"
                count={filteredOrdenes.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count}`
                }
                labelRowsPerPage="Filas por página:"
              />
            </Paper>
          )}
        </Box>
      ) : (
        /* Vista desktop - Tabla mejorada */
        <Paper sx={{ 
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid rgba(26, 35, 126, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}>
          <TableContainer>
            <Table>
              <TableHead sx={{
                background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)'
              }}>
                <TableRow>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 1.5 }}>
                    <TableSortLabel
                      active={orderBy === 'id_orden_servicio'}
                      direction={orderBy === 'id_orden_servicio' ? order : 'asc'}
                      onClick={createSortHandler('id_orden_servicio')}
                      sx={{ color: 'white !important', '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                    >
                      # OS
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 1.5 }}>Tipo</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 1.5 }}>Cliente</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 1.5 }}>Servicio</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 1.5 }}>
                    <TableSortLabel
                      active={orderBy === 'fecha_orden'}
                      direction={orderBy === 'fecha_orden' ? order : 'asc'}
                      onClick={createSortHandler('fecha_orden')}
                      sx={{ color: 'white !important', '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                    >
                      Fecha
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 1.5 }} align="right">Precio</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 1.5 }}>Tracker</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 1.5 }}>Estado</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 1.5 }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOrdenes
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((orden) => {
                    const ec = estadosConfig[parseInt(orden.id_estado_servicio)] || {};
                    const EIcon = ec.icon || AssignmentIcon;
                    const isPadre = esPadre(orden);
                    const isHija = esHija(orden);
                    const tracker = orden.trabajadores?.[0];
                    const servicio = orden.detalles?.[0];
                    if (orden.id_orden_padre || isPadre) console.log('>>> Orden delivery:', { id: orden.id_orden_servicio, tipo_servicio: orden.tipo_servicio, id_orden_padre: orden.id_orden_padre, isPadre, isHija });

                    return (
                      <TableRow
                        key={orden.id_orden_servicio}
                        sx={{
                          bgcolor: isPadre ? 'rgba(30,39,131,0.04)' : isHija ? 'rgba(249,250,251,1)' : 'inherit',
                          '&:hover': { backgroundColor: 'rgba(79, 195, 247, 0.08)' },
                          borderLeft: isPadre ? '3px solid #1A237E' : isHija ? '3px solid #C7D2FE' : 'none',
                        }}
                      >
                        {/* #OS con sigla */}
                        <TableCell sx={{ py: 1.2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1A237E' }}>
                              {orden.id_orden_servicio}
                            </Typography>
                            {isPadre && (
                              <Chip label="MASTER" size="small"
                                icon={<DeliveryIcon sx={{ fontSize: 12 }} />}
                                sx={{ height: 20, fontSize: 9, fontWeight: 800, bgcolor: '#1A237E', color: '#fff',
                                  '& .MuiChip-icon': { color: '#fff' } }} />
                            )}
                            {isHija && (
                              <Chip label={`(${orden.id_orden_padre})`} size="small"
                                sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: '#EEF2FF', color: '#1A237E' }} />
                            )}
                          </Box>
                        </TableCell>

                        {/* Tipo */}
                        <TableCell sx={{ py: 1.2 }}>
                          <Typography variant="caption" fontWeight={600}>
                            {orden.tipo_servicio || '-'}
                          </Typography>
                          {isHija && orden.orden_secuencia && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              Entrega #{orden.orden_secuencia}
                            </Typography>
                          )}
                        </TableCell>

                        {/* Cliente */}
                        <TableCell sx={{ py: 1.2 }}>
                          {isPadre ? (
                            <Typography variant="caption" color="text.secondary" fontStyle="italic">Ruta agrupadora</Typography>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Avatar sx={{ width: 24, height: 24, fontSize: '0.65rem', fontWeight: 'bold',
                                background: 'linear-gradient(135deg, #4FC3F7, #29B6F6)', color: '#fff' }}>
                                {orden.nombre_cliente?.[0]}{orden.apellido_cliente?.[0]}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 140 }}>
                                  {orden.nombre_cliente} {orden.apellido_cliente}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </TableCell>

                        {/* Servicio */}
                        <TableCell sx={{ py: 1.2 }}>
                          <Typography variant="caption" noWrap sx={{ maxWidth: 120, display: 'block' }}>
                            {servicio?.nombre_servicio || '-'}
                          </Typography>
                          {orden.detalle_entrega && (
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 120, display: 'block' }}>
                              {orden.detalle_entrega}
                            </Typography>
                          )}
                        </TableCell>

                        {/* Fecha */}
                        <TableCell sx={{ py: 1.2 }}>
                          <Typography variant="body2">
                            {new Date(orden.fecha_orden).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(orden.fecha_orden).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </TableCell>

                        {/* Precio */}
                        <TableCell align="right" sx={{ py: 1.2 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {orden.precio_total ? `S/ ${Number(orden.precio_total).toFixed(2)}` : '-'}
                          </Typography>
                        </TableCell>

                        {/* Tracker */}
                        <TableCell sx={{ py: 1.2 }}>
                          {tracker ? (
                            <Typography variant="caption" fontWeight={600} noWrap sx={{ maxWidth: 100, display: 'block' }}>
                              {tracker.nombre_trabajador} {tracker.apellido_trabajador?.[0]}.
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          )}
                        </TableCell>

                        {/* Estado */}
                        <TableCell sx={{ py: 1.2 }}>
                          <Chip
                            icon={<EIcon sx={{ fontSize: 13 }} />}
                            label={ec.nombre || orden.nombre_estado_servicio}
                            size="small"
                            sx={{ bgcolor: ec.bgColor, color: ec.color, fontWeight: 'bold', fontSize: 11 }}
                          />
                        </TableCell>

                        {/* Acciones */}
                        <TableCell sx={{ py: 1.2 }}>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title={isPadre || isHija ? 'Ver ruta' : 'Ver detalles'}>
                              <IconButton size="small" onClick={() => handleView(orden)}
                                sx={{ bgcolor: isPadre ? '#1A237E' : 'info.light', color: 'white', '&:hover': { bgcolor: isPadre ? '#0D47A1' : 'info.main' } }}>
                                {isPadre ? <DeliveryIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={isPadre || isHija ? 'Editar ruta' : 'Editar orden'}>
                              <IconButton size="small" onClick={() => handleEdit(orden)}
                                sx={{ bgcolor: 'primary.light', color: 'white', '&:hover': { bgcolor: 'primary.main' } }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {!isPadre && !isHija && (
                              <Tooltip title="Enviar link al cliente">
                                <IconButton size="small" onClick={() => openSendLinkDialog(orden)}
                                  sx={{ bgcolor: '#25D366', color: 'white', '&:hover': { bgcolor: '#128C7E' } }}>
                                  <SendIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {isPadre && (
                              <Tooltip title="Enviar tracking a clientes">
                                <IconButton size="small"
                                  onClick={() => openSendLinkDialog(orden)}
                                  sx={{ bgcolor: '#25D366', color: 'white', '&:hover': { bgcolor: '#128C7E' } }}>
                                  <SendIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title={isHija ? 'Eliminar entrega' : 'Eliminar orden'}>
                              <IconButton size="small" onClick={() => handleDelete(orden)}
                                sx={{ bgcolor: 'error.light', color: 'white', '&:hover': { bgcolor: 'error.main' } }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[20]}
            component="div"
            count={filteredOrdenes.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count}`
            }
            labelRowsPerPage="Filas por página:"
            sx={{
              borderTop: '1px solid rgba(26, 35, 126, 0.1)',
              backgroundColor: 'rgba(26, 35, 126, 0.02)'
            }}
          />
        </Paper>
      )}

      {/* FAB para móvil */}
      {isMobile && (
        <Fab
          color="primary"
          onClick={() => navigate('/home/ordenes/crear')}
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)',
            }
          }}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Dialog de detalles */}
      <DetalleDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        ordenId={selectedOrden?.id_orden_servicio}
      />

      {/* Dialog: Enviar Tracking */}
      <Dialog
        open={sendLinkDialog.open}
        onClose={() => !sendLinkLoading && setSendLinkDialog({ open: false, orden: null })}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #E2E8F0' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <SendIcon sx={{ color: '#1A237E' }} />
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                {trackingPreview?.es_delivery ? 'Enviar tracking a clientes' : 'Enviar seguimiento'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Orden #{sendLinkDialog.orden?.id_orden_servicio}
                {trackingPreview?.es_delivery ? ' — Ruta de delivery' : ' — Link válido 24h'}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {trackingPreviewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : trackingPreview?.es_delivery ? (
            /* DELIVERY MASTER: lista editable con check */
            <Box>
              {/* Header con seleccionar todos */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary">
                  {deliveryEmails.filter(e => e.checked && e.email && e.email.includes('@')).length} de {trackingPreview.destinatarios?.length} seleccionados para envío
                </Typography>
                <Button size="small" sx={{ fontSize: 11, textTransform: 'none' }}
                  onClick={() => {
                    const allChecked = deliveryEmails.every(e => e.checked);
                    setDeliveryEmails(prev => prev.map(e => ({ ...e, checked: !allChecked })));
                  }}>
                  {deliveryEmails.every(e => e.checked) ? 'Deseleccionar todos' : 'Seleccionar todos'}
                </Button>
              </Box>
              <Box sx={{ maxHeight: 400, overflowY: 'auto', border: '1px solid #E2E8F0', borderRadius: 2 }}>
                {trackingPreview.destinatarios?.map((d, idx) => {
                  const emailEdit = deliveryEmails.find(e => e.id_orden_servicio === d.id_orden_servicio);
                  const isChecked = emailEdit?.checked ?? false;
                  const waLink = getWhatsAppLink(d.telefono, d.nombre, d.id_orden_servicio);
                  return (
                    <Box key={d.id_orden_servicio}
                      sx={{ px: 1, py: 1.2, opacity: isChecked ? 1 : 0.45,
                        borderBottom: idx < trackingPreview.destinatarios.length - 1 ? '1px solid #F1F5F9' : 'none',
                        transition: 'opacity 0.2s' }}>
                      {/* Fila superior: check, #, nombre, estado, WhatsApp */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.8 }}>
                        <Checkbox size="small" checked={isChecked}
                          onChange={() => handleDeliveryCheckToggle(d.id_orden_servicio)}
                          sx={{ p: 0.3 }} />
                        <Chip label={d.secuencia || idx + 1} size="small"
                          sx={{ bgcolor: '#1A237E', color: '#fff', fontWeight: 700, minWidth: 28 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>{d.nombre || 'Sin nombre'}</Typography>
                          {d.detalle && (
                            <Typography variant="caption" color="text.secondary" noWrap>{d.detalle}</Typography>
                          )}
                        </Box>
                        {d.ultimo_envio ? (
                          <Tooltip title={`Enviado: ${new Date(d.ultimo_envio.fecha_envio).toLocaleString()} a ${d.ultimo_envio.email_destino}`}>
                            <Chip label={`${d.ultimo_envio.resultado === 'OK' ? 'Enviado' : 'Error'} ${new Date(d.ultimo_envio.fecha_envio).toLocaleDateString()}`}
                              size="small"
                              sx={{ fontSize: 10, fontWeight: 600, height: 22,
                                bgcolor: d.ultimo_envio.resultado === 'OK' ? '#DCFCE7' : '#FEE2E2',
                                color: d.ultimo_envio.resultado === 'OK' ? '#16A34A' : '#DC2626' }} />
                          </Tooltip>
                        ) : (
                          <Chip label="Nuevo" size="small"
                            sx={{ fontSize: 10, fontWeight: 600, height: 22, bgcolor: '#EEF2FF', color: '#4F46E5' }} />
                        )}
                        {waLink && (
                          <Tooltip title="Abrir WhatsApp Web">
                            <IconButton size="small" component="a" href={waLink} target="_blank" rel="noopener"
                              sx={{ bgcolor: '#25D366', color: '#fff', width: 28, height: 28, '&:hover': { bgcolor: '#128C7E' } }}>
                              <WhatsAppIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                      {/* Fila inferior: email editable + teléfono */}
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', pl: 4.5 }}>
                        <TextField size="small" fullWidth
                          placeholder="Email del cliente"
                          value={emailEdit?.email || ''}
                          onChange={(e) => handleDeliveryEmailChange(d.id_orden_servicio, e.target.value)}
                          disabled={!isChecked}
                          sx={{ '& .MuiInputBase-input': { fontSize: 13, py: 0.8 } }}
                        />
                        {d.telefono && (
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 90 }}>
                            {d.telefono}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
              {trackingPreview.destinatarios?.some(d => d.ultimo_envio) && (
                <Alert severity="info" sx={{ mt: 1.5, borderRadius: 2, fontSize: 12 }}>
                  Algunos clientes ya recibieron tracking. Al enviar se reenviará a los seleccionados.
                </Alert>
              )}
            </Box>
          ) : (
            /* SERVICIO NORMAL: un solo destinatario */
            <Box>
              {trackingPreview?.destinatarios?.[0] && (
                <Paper sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: '#F8FAFC' }}>
                  <Typography variant="body2" fontWeight={600}>
                    {trackingPreview.destinatarios[0].nombre || 'Cliente'}
                  </Typography>
                  {trackingPreview.destinatarios[0].ultimo_envio && (
                    <Alert severity="warning" sx={{ mt: 1, borderRadius: 1, py: 0, fontSize: 12 }}>
                      Último envío: {new Date(trackingPreview.destinatarios[0].ultimo_envio.fecha_envio).toLocaleString()}
                      {' a '}{trackingPreview.destinatarios[0].ultimo_envio.email_destino}
                    </Alert>
                  )}
                </Paper>
              )}
              <TextField
                fullWidth autoFocus
                label="Email del cliente"
                type="email"
                value={sendLinkEmail}
                onChange={(e) => setSendLinkEmail(e.target.value)}
                placeholder="cliente@ejemplo.com"
                size="small"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #E2E8F0' }}>
          <Button onClick={() => setSendLinkDialog({ open: false, orden: null })} disabled={sendLinkLoading}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={sendLinkLoading ? null : <SendIcon />}
            onClick={handleSendLink}
            disabled={sendLinkLoading || (!trackingPreview?.es_delivery && !sendLinkEmail) || (trackingPreview?.es_delivery && deliveryEmails.filter(e => e.checked && e.email && e.email.includes('@')).length === 0)}
            sx={{ bgcolor: '#1A237E', '&:hover': { bgcolor: '#0D47A1' } }}
          >
            {sendLinkLoading ? 'Enviando...' : trackingPreview?.es_delivery
              ? `Enviar a ${deliveryEmails.filter(e => e.checked && e.email && e.email.includes('@')).length} clientes`
              : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar de confirmación */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default OrdenesServicioAdmin;